// Items.tsx
import React, { useState, useEffect } from "react";
import * as S from "../../../../styles/components/configuration/sections/storesSection/Items";
import { itemsService, Item } from "../../../../services/item.service";
import { useCombinedStore } from "../../../../store";

interface ItemsProps {
  decryptedInfoToken: any;
  handleOpenModal: (type: string, object?: any) => void;
}

const Items: React.FC<ItemsProps> = ({
  decryptedInfoToken,
  handleOpenModal,
}) => {
  // Estados
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [typeFilter, setTypeFilter] = useState("all");
  const [storeFilter, setStoreFilter] = useState("all");
  const [items, setItems] = useState<Item[]>([]);
  const [organizationView, setOrganizationView] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [expandedSections, setExpandedSections] = useState<{
    companies: string[];
    subsidiaries: string[];
    ambients: string[];
  }>({
    companies: [],
    subsidiaries: [],
    ambients: [],
  });
  const [viewMode, setViewMode] = useState<"hierarchical" | "grid" | "list">(
    "hierarchical"
  );
  const [showTutorial, setShowTutorial] = useState(true);

  const { authUserZustand } = useCombinedStore((state) => ({
    authUserZustand: state.authUserZustand,
  }));

  // Carregar dados
  useEffect(() => {
    if (decryptedInfoToken?.organization) {
      const allItems = itemsService.extractAllItems(
        decryptedInfoToken.organization
      );
      setItems(allItems);

      const orgView = itemsService.buildOrganizationView(
        decryptedInfoToken.organization
      );
      setOrganizationView(orgView);
      setLoading(false);

      // Expandir primeira empresa por padrão
      if (orgView.length > 0) {
        setExpandedSections((prev) => ({
          ...prev,
          companies: [orgView[0].companyName],
        }));
      }
    }
  }, [decryptedInfoToken]);

  // Handlers
  const handleDeleteItem = async (itemUuid: string) => {
    if (window.confirm("Tem certeza que deseja excluir este item?")) {
      try {
        const token = localStorage.getItem("token") || "";
        const success = await itemsService.deleteItem(itemUuid, token);

        if (success) {
          setItems((prev) => prev.filter((item) => item.itemUuid !== itemUuid));
          const orgView = itemsService.buildOrganizationView(
            decryptedInfoToken.organization
          );
          setOrganizationView(orgView);
          alert("Item excluído com sucesso!");
        } else {
          alert("Erro ao excluir item");
        }
      } catch (error) {
        console.error("Erro ao excluir item:", error);
        alert("Erro ao excluir item");
      }
    }
  };

  const toggleSection = (
    type: "companies" | "subsidiaries" | "ambients",
    id: string
  ) => {
    setExpandedSections((prev) => ({
      ...prev,
      [type]: prev[type].includes(id)
        ? prev[type].filter((item) => item !== id)
        : [...prev[type], id],
    }));
  };

  const toggleAllSections = (expand: boolean) => {
    if (expand) {
      const allCompanies = organizationView.map(
        (company) => company.companyName
      );
      const allSubsidiaries = organizationView.flatMap((company) =>
        company.subsidiaries.map((sub: any) => sub.subsidiaryName)
      );
      const allAmbients = organizationView.flatMap((company) =>
        company.subsidiaries.flatMap((sub: any) =>
          sub.ambients.map((amb: any) => amb.ambientUuid)
        )
      );

      setExpandedSections({
        companies: allCompanies,
        subsidiaries: allSubsidiaries,
        ambients: allAmbients,
      });
    } else {
      setExpandedSections({
        companies: [],
        subsidiaries: [],
        ambients: [],
      });
    }
  };

  // Filtrar itens
  const filteredItems = itemsService.filterItems(items, {
    searchTerm,
    statusFilter,
    typeFilter,
    storeFilter,
  });

  // Dados para filtros
  const uniqueTypes = itemsService.getUniqueTypes(items);
  const uniqueStores = itemsService.getUniqueStores(items);
  const stats = itemsService.calculateStats(items);

  // Componentes auxiliares
  const getStatusBadge = (status: boolean) => (
    <span
      style={{
        padding: "var(--sp-4) var(--sp-12)",
        borderRadius: "20px",
        fontSize: "var(--fs-xs)",
        fontWeight: 600,
        backgroundColor: status ? "#d4edda" : "#f8d7da",
        color: status ? "#155724" : "#721c24",
        border: `1px solid ${status ? "#c3e6cb" : "#f5c6cb"}`,
      }}
    >
      {status ? "Ativo" : "Inativo"}
    </span>
  );

  const getTypeBadge = (itemType: number) => {
    const colors = itemsService.getTypeColor(itemType);
    const typeText = itemsService.getItemTypeText(itemType);

    return (
      <span
        style={{
          padding: "var(--sp-4) var(--sp-8)",
          borderRadius: "12px",
          fontSize: "var(--fs-2xs)",
          fontWeight: 600,
          backgroundColor: colors.bg,
          color: colors.color,
          display: "flex",
          alignItems: "center",
          gap: "var(--sp-4)",
        }}
      >
        <S.MaterialIcons style={{ fontSize: "var(--fs-md)" }}>
          {colors.icon}
        </S.MaterialIcons>
        {typeText}
      </span>
    );
  };

  const getWarrantyBadge = (warrantyUntil: string) => {
    const isValid = itemsService.isWarrantyValid(warrantyUntil);
    return (
      <span
        style={{
          padding: "var(--sp-4) var(--sp-8)",
          borderRadius: "12px",
          fontSize: "var(--fs-2xs)",
          fontWeight: 600,
          backgroundColor: isValid ? "#d4edda" : "#f8d7da",
          color: isValid ? "#155724" : "#721c24",
          display: "flex",
          alignItems: "center",
          gap: "var(--sp-4)",
        }}
      >
        <S.MaterialIcons style={{ fontSize: "var(--fs-md)" }}>
          {isValid ? "verified" : "warning"}
        </S.MaterialIcons>
        {isValid ? "Garantia OK" : "Garantia Expirada"}
      </span>
    );
  };

  // Componente ItemCard
  const ItemCard = ({ item }: { item: Item }) => (
    <S.ItemCard onClick={() => handleOpenModal("item", item)}>
      {/* Badge de status */}
      <div
        style={{
          position: "absolute",
          top: "calc(var(--u) * 12)",
          right: "calc(var(--u) * 12)",
          display: "flex",
          gap: "var(--sp-8)",
        }}
      >
        {getTypeBadge(item.itemType)}
        {getStatusBadge(item.itemStatus)}
      </div>

      {/* Título e descrição */}
      <div style={{ marginBottom: "var(--sp-12)", paddingRight: "calc(var(--u) * 100)" }}>
        <h3
          style={{
            margin: "0 0 var(--sp-8) 0",
            fontSize: "var(--fs-base)",
            fontWeight: 600,
            color: "#1a1a1a",
            display: "flex",
            alignItems: "center",
            gap: "var(--sp-8)",
          }}
        >
          <S.MaterialIcons style={{ color: "#0066cc", fontSize: "var(--fs-lg)" }}>
            inventory_2
          </S.MaterialIcons>
          {item.itemName}
        </h3>
        {item.itemDescription && (
          <p
            style={{
              margin: 0,
              fontSize: "var(--fs-md)",
              color: "#6c757d",
              lineHeight: "1.4",
              display: "flex",
              alignItems: "flex-start",
              gap: "var(--sp-6)",
            }}
          >
            <S.MaterialIcons style={{ fontSize: "var(--fs-md)", marginTop: "var(--sp-2)" }}>
              description
            </S.MaterialIcons>
            {item.itemDescription}
          </p>
        )}
      </div>

      {/* Informações principais */}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "1fr 1fr",
          gap: "var(--sp-12)",
          marginBottom: "var(--sp-12)",
        }}
      >
        <div>
          <div
            style={{
              fontSize: "var(--fs-xs)",
              color: "#6c757d",
              marginBottom: "var(--sp-4)",
              display: "flex",
              alignItems: "center",
              gap: "var(--sp-4)",
            }}
          >
            <S.MaterialIcons style={{ fontSize: "var(--fs-md)" }}>
              fingerprint
            </S.MaterialIcons>
            Nº Série
          </div>
          <div
            style={{
              fontSize: "var(--fs-sm)",
              fontWeight: 500,
              fontFamily: "monospace",
              padding: "var(--sp-4) var(--sp-8)",
              backgroundColor: "#f8f9fa",
              borderRadius: "4px",
            }}
          >
            {item.serialNumber}
          </div>
        </div>
        <div>
          <div
            style={{
              fontSize: "var(--fs-xs)",
              color: "#6c757d",
              marginBottom: "var(--sp-4)",
              display: "flex",
              alignItems: "center",
              gap: "var(--sp-4)",
            }}
          >
            <S.MaterialIcons style={{ fontSize: "var(--fs-md)" }}>
              verified_user
            </S.MaterialIcons>
            Garantia
          </div>
          {getWarrantyBadge(item.warrantyUntil)}
        </div>
      </div>

      {/* Localização */}
      <div
        style={{
          backgroundColor: "#f8f9fa",
          padding: "var(--sp-8) var(--sp-12)",
          borderRadius: "6px",
          marginBottom: "var(--sp-12)",
          border: "1px solid #e9ecef",
        }}
      >
        <div
          style={{
            fontSize: "var(--fs-2xs)",
            color: "#6c757d",
            marginBottom: "var(--sp-4)",
            display: "flex",
            alignItems: "center",
            gap: "var(--sp-4)",
          }}
        >
          <S.MaterialIcons style={{ fontSize: "var(--fs-xs)" }}>
            location_on
          </S.MaterialIcons>
          Localização
        </div>
        <div style={{ fontSize: "var(--fs-xs)", fontWeight: 500 }}>
          {item.ambientName} • {item.storeName}
        </div>
      </div>

      {/* Datas */}
      <div
        style={{
          fontSize: "var(--fs-xs)",
          color: "#6c757d",
          borderTop: "1px solid #f1f3f4",
          paddingTop: "var(--sp-12)",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <div style={{ display: "flex", flexDirection: "column", gap: "var(--sp-2)" }}>
          <div style={{ display: "flex", alignItems: "center", gap: "var(--sp-4)" }}>
            <S.MaterialIcons style={{ fontSize: "var(--fs-xs)" }}>
              event
            </S.MaterialIcons>
            Compra: {new Date(item.purchaseDate).toLocaleDateString("pt-BR")}
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: "var(--sp-4)" }}>
            <S.MaterialIcons style={{ fontSize: "var(--fs-xs)" }}>
              update
            </S.MaterialIcons>
            Atualizado: {new Date(item.updatedAt).toLocaleDateString("pt-BR")}
          </div>
        </div>
      </div>

      {/* Ações */}
      <div style={{ display: "flex", gap: "var(--sp-8)", marginTop: "var(--sp-12)" }}>
        <S.Button
          onClick={(e) => {
            e.stopPropagation();
            handleOpenModal("item", item);
          }}
          style={{
            padding: "var(--sp-6) var(--sp-12)",
            fontSize: "var(--fs-xs)",
            flex: 1,
            minWidth: "auto",
          }}
        >
          <S.MaterialIcons style={{ fontSize: "var(--fs-md)" }}>edit</S.MaterialIcons>
          Editar
        </S.Button>
        <S.SecondaryButton
          onClick={(e) => {
            e.stopPropagation();
            handleDeleteItem(item.itemUuid);
          }}
          style={{
            padding: "var(--sp-6) var(--sp-12)",
            fontSize: "var(--fs-xs)",
          }}
        >
          <S.MaterialIcons style={{ fontSize: "var(--fs-md)" }}>delete</S.MaterialIcons>
          Excluir
        </S.SecondaryButton>
      </div>
    </S.ItemCard>
  );

  // Componente Tutorial
  const WelcomeGuide = () => (
    <S.WelcomeGuide>
      <S.WelcomeCloseButton onClick={() => setShowTutorial(false)}>
        ×
      </S.WelcomeCloseButton>

      <S.WelcomeContent>
        <S.WelcomeIcon>
          <S.MaterialIcons>help</S.MaterialIcons>
        </S.WelcomeIcon>

        <S.WelcomeText>
          <h3>Bem-vindo ao Gerenciador de Itens!</h3>
          <p>
            Aqui você pode visualizar e gerenciar todos os itens da sua
            organização de forma intuitiva.
          </p>

          <S.WelcomeTipsGrid>
            <S.WelcomeTip>
              <S.MaterialIcons>search</S.MaterialIcons>
              <span>Use a busca para encontrar itens específicos</span>
            </S.WelcomeTip>
            <S.WelcomeTip>
              <S.MaterialIcons>filter_alt</S.MaterialIcons>
              <span>Filtre por status, tipo ou loja</span>
            </S.WelcomeTip>
            <S.WelcomeTip>
              <S.MaterialIcons>add_circle</S.MaterialIcons>
              <span>Clique em "Novo Item" para adicionar</span>
            </S.WelcomeTip>
            <S.WelcomeTip>
              <S.MaterialIcons>expand_more</S.MaterialIcons>
              <span>Clique nas setas para expandir seções</span>
            </S.WelcomeTip>
          </S.WelcomeTipsGrid>
        </S.WelcomeText>
      </S.WelcomeContent>
    </S.WelcomeGuide>
  );

  // Renderizar hierarquia
  const renderHierarchicalView = () => {
    if (organizationView.length === 0) {
      return (
        <S.EmptyState>
          <S.EmptyStateIcon>📦</S.EmptyStateIcon>
          <S.EmptyStateTitle>Nenhuma empresa cadastrada</S.EmptyStateTitle>
          <S.EmptyStateDescription>
            Comece adicionando uma empresa e seus itens à sua organização.
          </S.EmptyStateDescription>
          <S.Button onClick={() => handleOpenModal("company")}>
            <S.MaterialIcons>add</S.MaterialIcons>
            Adicionar Primeira Empresa
          </S.Button>
        </S.EmptyState>
      );
    }

    return organizationView.map((company, companyIndex) => {
      const isCompanyExpanded = expandedSections.companies.includes(
        company.companyName
      );
      const totalItems = company.subsidiaries.reduce(
        (total: number, sub: any) =>
          total +
          sub.ambients.reduce(
            (ambTotal: number, amb: any) => ambTotal + amb.items.length,
            0
          ),
        0
      );

      return (
        <div key={`company-${companyIndex}`} style={{ marginBottom: "var(--sp-20)" }}>
          {/* Cabeçalho da Empresa */}
          <S.HierarchyHeader
            $expanded={isCompanyExpanded}
            $level={0}
            onClick={() => toggleSection("companies", company.companyName)}
          >
            <S.HierarchyInfo>
              <S.HierarchyIcon $level={0} $expanded={isCompanyExpanded}>
                <S.MaterialIcons style={{ fontSize: "var(--fs-2xl)" }}>
                  business
                </S.MaterialIcons>
              </S.HierarchyIcon>
              <div>
                <S.HierarchyTitle $level={0}>
                  {company.companyName}
                </S.HierarchyTitle>
                <S.HierarchyMeta>
                  <span
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "var(--sp-4)",
                    }}
                  >
                    <S.MaterialIcons style={{ fontSize: "var(--fs-base)" }}>
                      store
                    </S.MaterialIcons>
                    {company.subsidiaries.length} loja(s)
                  </span>
                  <span
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "var(--sp-4)",
                    }}
                  >
                    <S.MaterialIcons style={{ fontSize: "var(--fs-base)" }}>
                      inventory_2
                    </S.MaterialIcons>
                    {totalItems} item(ns)
                  </span>
                </S.HierarchyMeta>
              </div>
            </S.HierarchyInfo>

            <div style={{ display: "flex", gap: "var(--sp-12)", alignItems: "center" }}>
              <S.Button
                onClick={(e) => {
                  e.stopPropagation();
                  const allSubsidiaries = company.subsidiaries.map(
                    (sub: any) => sub.subsidiaryName
                  );
                  const allAmbients = company.subsidiaries.flatMap((sub: any) =>
                    sub.ambients.map((amb: any) => amb.ambientUuid)
                  );

                  if (isCompanyExpanded) {
                    setExpandedSections((prev) => ({
                      ...prev,
                      companies: prev.companies.filter(
                        (c) => c !== company.companyName
                      ),
                      subsidiaries: prev.subsidiaries.filter(
                        (s) => !allSubsidiaries.includes(s)
                      ),
                      ambients: prev.ambients.filter(
                        (a) => !allAmbients.includes(a)
                      ),
                    }));
                  } else {
                    setExpandedSections((prev) => ({
                      ...prev,
                      companies: [...prev.companies, company.companyName],
                      subsidiaries: [...prev.subsidiaries, ...allSubsidiaries],
                      ambients: [...prev.ambients, ...allAmbients],
                    }));
                  }
                }}
                style={{
                  padding: "var(--sp-8) var(--sp-16)",
                  fontSize: "var(--fs-sm)",
                  minWidth: "auto",
                }}
              >
                <S.MaterialIcons style={{ fontSize: "var(--fs-base)" }}>
                  {isCompanyExpanded ? "unfold_less" : "unfold_more"}
                </S.MaterialIcons>
                {isCompanyExpanded ? "Recolher Tudo" : "Expandir Tudo"}
              </S.Button>
              <S.ExpandIcon $expanded={isCompanyExpanded}>
                <S.MaterialIcons style={{ fontSize: "var(--fs-2xl)" }}>
                  expand_more
                </S.MaterialIcons>
              </S.ExpandIcon>
            </div>
          </S.HierarchyHeader>

          {/* Conteúdo da Empresa */}
          {isCompanyExpanded && (
            <S.HierarchySection $level={1}>
              {company.subsidiaries.map(
                (subsidiary: any, subsidiaryIndex: number) => {
                  const isSubsidiaryExpanded =
                    expandedSections.subsidiaries.includes(
                      subsidiary.subsidiaryName
                    );
                  const subsidiaryItems = subsidiary.ambients.reduce(
                    (total: number, amb: any) => total + amb.items.length,
                    0
                  );

                  return (
                    <div
                      key={`subsidiary-${companyIndex}-${subsidiaryIndex}`}
                      style={{ marginBottom: "var(--sp-20)" }}
                    >
                      {/* Cabeçalho da Loja */}
                      <S.HierarchyHeader
                        $expanded={isSubsidiaryExpanded}
                        $level={1}
                        onClick={() =>
                          toggleSection(
                            "subsidiaries",
                            subsidiary.subsidiaryName
                          )
                        }
                      >
                        <S.HierarchyInfo>
                          <S.HierarchyIcon
                            $level={1}
                            $expanded={isSubsidiaryExpanded}
                          >
                            <S.MaterialIcons style={{ fontSize: "var(--fs-xl)" }}>
                              store
                            </S.MaterialIcons>
                          </S.HierarchyIcon>
                          <div>
                            <S.HierarchyTitle $level={1}>
                              {subsidiary.subsidiaryName}
                            </S.HierarchyTitle>
                            <S.HierarchyMeta>
                              <span
                                style={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: "var(--sp-4)",
                                }}
                              >
                                <S.MaterialIcons style={{ fontSize: "var(--fs-md)" }}>
                                  meeting_room
                                </S.MaterialIcons>
                                {subsidiary.ambients.length} ambiente(s)
                              </span>
                              <span
                                style={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: "var(--sp-4)",
                                }}
                              >
                                <S.MaterialIcons style={{ fontSize: "var(--fs-md)" }}>
                                  inventory_2
                                </S.MaterialIcons>
                                {subsidiaryItems} item(ns)
                              </span>
                            </S.HierarchyMeta>
                          </div>
                        </S.HierarchyInfo>
                        <S.ExpandIcon $expanded={isSubsidiaryExpanded}>
                          <S.MaterialIcons style={{ fontSize: "var(--fs-xl)" }}>
                            expand_more
                          </S.MaterialIcons>
                        </S.ExpandIcon>
                      </S.HierarchyHeader>

                      {/* Conteúdo da Loja */}
                      {isSubsidiaryExpanded && (
                        <S.HierarchySection $level={2}>
                          {subsidiary.ambients.map(
                            (ambient: any, ambientIndex: number) => {
                              const isAmbientExpanded =
                                expandedSections.ambients.includes(
                                  ambient.ambientUuid
                                );
                              const ambientItems = ambient.items.filter(
                                (item: Item) =>
                                  filteredItems.some(
                                    (filteredItem) =>
                                      filteredItem.itemUuid === item.itemUuid
                                  )
                              );

                              if (
                                ambientItems.length === 0 &&
                                filteredItems.length > 0
                              )
                                return null;

                              return (
                                <div
                                  key={`ambient-${companyIndex}-${subsidiaryIndex}-${ambientIndex}`}
                                  style={{ marginBottom: "var(--sp-20)" }}
                                >
                                  {/* Cabeçalho do Ambiente */}
                                  <S.HierarchyHeader
                                    $expanded={isAmbientExpanded}
                                    $level={2}
                                    onClick={() =>
                                      toggleSection(
                                        "ambients",
                                        ambient.ambientUuid
                                      )
                                    }
                                  >
                                    <S.HierarchyInfo>
                                      <S.HierarchyIcon
                                        $level={2}
                                        $expanded={isAmbientExpanded}
                                      >
                                        <S.MaterialIcons
                                          style={{ fontSize: "var(--fs-lg)" }}
                                        >
                                          meeting_room
                                        </S.MaterialIcons>
                                      </S.HierarchyIcon>
                                      <div>
                                        <S.HierarchyTitle $level={2}>
                                          {ambient.ambientName}
                                        </S.HierarchyTitle>
                                        <S.HierarchyMeta>
                                          <span
                                            style={{
                                              display: "flex",
                                              alignItems: "center",
                                              gap: "var(--sp-4)",
                                            }}
                                          >
                                            <S.MaterialIcons
                                              style={{ fontSize: "var(--fs-xs)" }}
                                            >
                                              inventory_2
                                            </S.MaterialIcons>
                                            {ambientItems.length} item(ns)
                                          </span>
                                        </S.HierarchyMeta>
                                      </div>
                                    </S.HierarchyInfo>
                                    <S.ExpandIcon $expanded={isAmbientExpanded}>
                                      <S.MaterialIcons
                                        style={{ fontSize: "var(--fs-lg)" }}
                                      >
                                        expand_more
                                      </S.MaterialIcons>
                                    </S.ExpandIcon>
                                  </S.HierarchyHeader>

                                  {/* Itens do Ambiente */}
                                  {isAmbientExpanded &&
                                    ambientItems.length > 0 && (
                                      <S.ItemsGrid
                                        style={{ marginTop: "var(--sp-16)" }}
                                      >
                                        {ambientItems.map((item: Item) => (
                                          <ItemCard
                                            key={item.itemUuid}
                                            item={item}
                                          />
                                        ))}
                                      </S.ItemsGrid>
                                    )}

                                  {isAmbientExpanded &&
                                    ambientItems.length === 0 && (
                                      <S.EmptyState
                                        style={{ padding: "var(--sp-40) var(--sp-20)" }}
                                      >
                                        <S.EmptyStateIcon
                                          style={{ fontSize: "var(--fs-6xl)" }}
                                        >
                                          📦
                                        </S.EmptyStateIcon>
                                        <S.EmptyStateTitle
                                          style={{ fontSize: "var(--fs-base)" }}
                                        >
                                          Nenhum item neste ambiente
                                        </S.EmptyStateTitle>
                                        <S.Button
                                          onClick={() =>
                                            handleOpenModal("item", { ambient })
                                          }
                                          style={{ marginTop: "var(--sp-12)" }}
                                        >
                                          <S.MaterialIcons>add</S.MaterialIcons>
                                          Adicionar Item
                                        </S.Button>
                                      </S.EmptyState>
                                    )}
                                </div>
                              );
                            }
                          )}
                        </S.HierarchySection>
                      )}
                    </div>
                  );
                }
              )}
            </S.HierarchySection>
          )}
        </div>
      );
    });
  };

  // Renderizar grid view
  const renderGridView = () => (
    <S.ItemsGrid>
      {filteredItems.map((item) => (
        <ItemCard key={item.itemUuid} item={item} />
      ))}
    </S.ItemsGrid>
  );

  // Render principal
  return (
    <S.FieldsContainer>
      {/* Cabeçalho */}
      <S.Header>
        <S.HeaderContent>
          <S.HeaderTitle>
            <S.MaterialIcons style={{ fontSize: "calc(var(--u) * 36)" }}>
              inventory_2
            </S.MaterialIcons>
            Itens
          </S.HeaderTitle>
          <S.HeaderSubtitle>
            Organize e controle todos os itens da sua organização
          </S.HeaderSubtitle>
        </S.HeaderContent>

        <S.HeaderActions>
          {/* Toggle View Mode */}
          <S.ViewModeToggle>
            <S.ViewModeButton
              $isActive={viewMode === "hierarchical"}
              onClick={() => setViewMode("hierarchical")}
            >
              <S.MaterialIcons style={{ fontSize: "var(--fs-base)" }}>
                account_tree
              </S.MaterialIcons>
              Hierarquia
            </S.ViewModeButton>
            <S.ViewModeButton
              $isActive={viewMode === "grid"}
              onClick={() => setViewMode("grid")}
            >
              <S.MaterialIcons style={{ fontSize: "var(--fs-base)" }}>
                grid_view
              </S.MaterialIcons>
              Grid
            </S.ViewModeButton>
          </S.ViewModeToggle>

          {viewMode === "hierarchical" && (
            <S.Button
              onClick={() =>
                toggleAllSections(expandedSections.companies.length === 0)
              }
            >
              <S.MaterialIcons>
                {expandedSections.companies.length === 0
                  ? "unfold_more"
                  : "unfold_less"}
              </S.MaterialIcons>
              {expandedSections.companies.length === 0
                ? "Expandir Tudo"
                : "Recolher Tudo"}
            </S.Button>
          )}

          <S.Button onClick={() => handleOpenModal("item")}>
            <S.MaterialIcons>add</S.MaterialIcons>
            Novo Item
          </S.Button>
        </S.HeaderActions>
      </S.Header>

      {/* Tutorial */}
      {showTutorial && <WelcomeGuide />}

      {/* Filtros */}
      <S.FilterSection>
        <S.SearchWrapper>
          <S.SearchIcon>
            <S.MaterialIcons>search</S.MaterialIcons>
          </S.SearchIcon>
          <S.SearchInput
            type="text"
            placeholder="Buscar por nome, descrição, serial, ambiente ou loja..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
          {searchTerm && (
            <S.ClearButton onClick={() => setSearchTerm("")}>
              <S.MaterialIcons>close</S.MaterialIcons>
            </S.ClearButton>
          )}
        </S.SearchWrapper>

        <S.FilterSelect
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
        >
          <option value="all">Todos os status</option>
          <option value="active">Ativos</option>
          <option value="inactive">Inativos</option>
        </S.FilterSelect>

        <S.FilterSelect
          value={typeFilter}
          onChange={(e) => setTypeFilter(e.target.value)}
        >
          <option value="all">Todos os tipos</option>
          {uniqueTypes.map((type) => (
            <option key={type} value={type}>
              {type}
            </option>
          ))}
        </S.FilterSelect>

        <S.FilterSelect
          value={storeFilter}
          onChange={(e) => setStoreFilter(e.target.value)}
        >
          <option value="all">Todas as lojas</option>
          {uniqueStores.map((store) => (
            <option key={store} value={store}>
              {store}
            </option>
          ))}
        </S.FilterSelect>
      </S.FilterSection>

      {/* Estatísticas */}
      <S.StatsGrid>
        <S.StatCard $color="#0066cc">
          <S.StatIcon>📦</S.StatIcon>
          <S.StatValue>{stats.total}</S.StatValue>
          <S.StatLabel>Total de Itens</S.StatLabel>
        </S.StatCard>

        <S.StatCard $color="#28a745">
          <S.StatIcon>✅</S.StatIcon>
          <S.StatValue>{stats.active}</S.StatValue>
          <S.StatLabel>Itens Ativos</S.StatLabel>
        </S.StatCard>

        <S.StatCard $color="#ffc107">
          <S.StatIcon>🏷️</S.StatIcon>
          <S.StatValue>{stats.types}</S.StatValue>
          <S.StatLabel>Tipos Diferentes</S.StatLabel>
        </S.StatCard>

        <S.StatCard $color="#dc3545">
          <S.StatIcon>⚠️</S.StatIcon>
          <S.StatValue>{stats.expiredWarranties}</S.StatValue>
          <S.StatLabel>Garantias Expiradas</S.StatLabel>
        </S.StatCard>
      </S.StatsGrid>

      {/* Conteúdo */}
      {loading ? (
        <S.LoadingContainer>
          <S.LoadingSpinner />
          <div>Carregando itens...</div>
        </S.LoadingContainer>
      ) : filteredItems.length > 0 ? (
        <>
          {viewMode === "hierarchical"
            ? renderHierarchicalView()
            : renderGridView()}
          <S.ResultsCounter>
            Mostrando <strong>{filteredItems.length}</strong> de{" "}
            <strong>{items.length}</strong> item{items.length !== 1 ? "s" : ""}
          </S.ResultsCounter>
        </>
      ) : (
        <S.EmptyState>
          <S.EmptyStateIcon>🔍</S.EmptyStateIcon>
          <S.EmptyStateTitle>
            {searchTerm ? "Nenhum item encontrado" : "Nenhum item cadastrado"}
          </S.EmptyStateTitle>
          <S.EmptyStateDescription>
            {searchTerm
              ? "Tente ajustar os filtros de busca"
              : "Comece criando seu primeiro item"}
          </S.EmptyStateDescription>
          {!searchTerm && (
            <S.Button
              onClick={() => handleOpenModal("item")}
              style={{ marginTop: "var(--sp-16)" }}
            >
              <S.MaterialIcons>add</S.MaterialIcons>
              Criar Primeiro Item
            </S.Button>
          )}
        </S.EmptyState>
      )}
    </S.FieldsContainer>
  );
};

export default Items;
